import {
  HEC_SRC_COOKIE_NAME,
  HEC_SRC_MAX_AGE_SECONDS,
  parseHecSrcValue,
} from "./hec-src";

/**
 * `hec_src` soft-navigation refresh (COM-787).
 *
 * The server refresh (`hec-src.server.ts`, branch 2) only runs when a request
 * reaches `SessionManagementMiddleware`. A soft navigation whose `.data`
 * response is an edge-cache HIT, or that fetches no server data at all, never
 * does — and the inline backstop only runs on a hard document load. Long
 * soft-nav browses therefore aged the cookie out before the `/checkout` hard
 * load, and the purchase resolved `(not set)`.
 *
 * This re-emits the STORED value verbatim with a fresh `Max-Age`, mirroring
 * the inline backstop's refresh branch (`if(raw&&parse(raw)){write(raw)}`)
 * byte for byte: same attributes, same "unparseable → let it age out" rule.
 * It never captures, merges or overwrites — attribution capture stays with
 * the server builder and the inline backstop, so COM-510's merge semantics are
 * untouched.
 */

/** The cookie string a refresh writes for `raw`, or null when it must not. */
export function buildHecSrcRefreshCookie(
  raw: string | null,
  isHttps: boolean,
): string | null {
  if (!raw || !parseHecSrcValue(raw)) return null;
  return (
    `${HEC_SRC_COOKIE_NAME}=${raw}; path=/; max-age=${HEC_SRC_MAX_AGE_SECONDS}; samesite=lax` +
    (isHttps ? "; secure" : "")
  );
}

function readRawHecSrc(): string | null {
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${HEC_SRC_COOKIE_NAME}=([^;]*)`),
  );
  return match ? match[1] : null;
}

/** Refresh the live cookie's window. Fail-silent: attribution is best-effort. */
export function refreshHecSrcCookie(): void {
  try {
    const cookie = buildHecSrcRefreshCookie(
      readRawHecSrc(),
      location.protocol === "https:",
    );
    if (cookie) document.cookie = cookie;
  } catch {
    // Cookie access can throw (sandboxed/privacy contexts) — never break nav.
  }
}
